// Viewport / input-type hooks for the console's mobile rules. These mirror the
// CSS variants in tailwind.config.cjs (`coarse:`, `phone:`, `landphone:`) so a
// component can pick ONE render path (bottom sheet vs side sheet, cards vs
// table) instead of mounting both and hiding one with CSS.
//
// `<html data-pointer="coarse">` is honoured everywhere the (pointer: coarse)
// media query is — it is the testable fallback (headless browsers report a fine
// pointer) and the opt-in for desktop touch screens.

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

const SM_MAX = "(max-width: 639.98px)";
const SHORT = "(max-height: 500px)";
const COARSE = "(pointer: coarse)";
const LANDSCAPE_PHONE = "(max-height: 500px) and (orientation: landscape)";
const PORTRAIT = "(orientation: portrait)";

function matches(query: string): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
}

function attrCoarse(): boolean {
  return typeof document !== "undefined" && document.documentElement.dataset.pointer === "coarse";
}

/** Subscribe to a media query (and the html[data-pointer] attribute, which the coarse rules also honour). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
      const mq = window.matchMedia(query);
      mq.addEventListener("change", cb);
      const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(cb) : null;
      mo?.observe(document.documentElement, { attributes: true, attributeFilter: ["data-pointer"] });
      return () => {
        mq.removeEventListener("change", cb);
        mo?.disconnect();
      };
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => matches(query), () => false);
}

/** Coarse pointer: real (pointer: coarse) or the <html data-pointer="coarse"> fallback. */
export function usePointerCoarse(): boolean {
  const media = useMediaQuery(COARSE);
  const attr = useSyncExternalStore(subscribeAttr, attrCoarse, () => false);
  return media || attr;
}

function subscribeAttr(cb: () => void): () => void {
  if (typeof MutationObserver === "undefined" || typeof document === "undefined") return () => {};
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-pointer"] });
  return () => mo.disconnect();
}

/** Narrower than `sm` (640 px) — the width at which tables become cards and drawers become bottom sheets. */
export function useIsPhoneWidth(): boolean {
  return useMediaQuery(SM_MAX);
}

/**
 * A phone in either orientation: below `sm`, or a coarse pointer on a short
 * (≤ 500 px) screen. Drives the bottom tab bar, the 28 px staleness line and
 * the chart floor — the same condition as the `phone:` Tailwind variant.
 */
export function usePhone(): boolean {
  const narrow = useMediaQuery(SM_MAX);
  const short = useMediaQuery(SHORT);
  const coarse = usePointerCoarse();
  return narrow || (short && coarse);
}

/** Landscape phone (≤ 500 px tall) — header 48 px, side sheets. */
export function useLandscapePhone(): boolean {
  return useMediaQuery(LANDSCAPE_PHONE);
}

export function usePortrait(): boolean {
  return useMediaQuery(PORTRAIT);
}

/**
 * True until the first client render settles — lets a component that depends on
 * a media query avoid a flash when the server/SSR fallback (false) differs.
 */
export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}
